import React from 'react';

import { formatMoney } from '@betterment/js-runtime';
import { Box, Icon, ListRow, Text } from '@betterment/samba-react';

import { ManagedSubAccountRows } from './ManagedSubAccountRows';

import { GainsLosses } from '../../../../../Shared/components/GainsLosses';
import { StyledLinkRow, titleCss } from '../../../../../Shared/components-v2/styled-link-row/StyledLinkRow';
import { ThemedImage } from '../../../../../Shared/components-v2/themed-image/ThemedImage';
import { urlBuilder } from '../../../../../Shared/urlBuilder';
import { PurposeAccountsCardQuery } from '../../graphql/purposeAccountsCard.generated';

type EnvelopeAccount = Extract<
  PurposeAccountsCardQuery['purpose']['envelope']['accounts'][number],
  { __typename: 'CashAccount' | 'ManagedAccount' }
>;
type Props = { account: EnvelopeAccount; hasRecurringTransaction: boolean };

const MANAGED_ACCOUNT_TYPE = 'ManagedAccount';
const CASH_ACCOUNT_TYPE = 'CashAccount';

export const EnvelopeAccountRow: React.FC<Props> = ({ account, hasRecurringTransaction }) => {
  const { name, balance } = account;
  const earned = 'performanceTotals' in account ? account.performanceTotals.earned : null;
  const rowSubtext = getRowSubtext(account);
  const hasMultipleSubAccounts = account.__typename === 'ManagedAccount' && account.subAccounts.length > 1;

  return (
    <StyledLinkRow to={urlBuilder.appAccount.absolute({ accountId: account.id })} name={name}>
      <ListRow.LeadingAsset>
        <Box css={{ width: '36px', height: '36px' }}>
          <ThemedImage themedAsset={account.icon} alt={`${name} icon`} />
        </Box>
      </ListRow.LeadingAsset>
      <ListRow.LeadingContent>
        <Text color="text" css={titleCss}>
          {name}
        </Text>
        <Text color="text.weak" variant="text50">
          {rowSubtext}
        </Text>
      </ListRow.LeadingContent>
      <ListRow.TrailingContent css={{ alignItems: 'end' }}>
        <Box css={{ display: 'flex', alignItems: 'center', height: '100%', gap: 'space.100' }}>
          {hasRecurringTransaction && <Icon name="ArrowCircle" color="icon.success" size="xsmall" />}
          {balance && <Text color="text">{formatMoney(balance)}</Text>}
        </Box>
        {earned && <GainsLosses amount={earned} />}
      </ListRow.TrailingContent>
      {hasMultipleSubAccounts && (
        <ListRow.BottomContent css={{ marginTop: 'space.300', gridColumnStart: '2', gridColumnEnd: '4' }}>
          <ManagedSubAccountRows account={account} />
        </ListRow.BottomContent>
      )}
    </StyledLinkRow>
  );
};

function getRowSubtext(account: EnvelopeAccount) {
  switch (account.__typename) {
    case CASH_ACCOUNT_TYPE:
      return `${account.apy.rate}% APY*`;
    case MANAGED_ACCOUNT_TYPE:
      return account.portfolio?.configurationDescription;
    default:
      return undefined;
  }
}
